<template>
  <div>
    <b-dropdown text="Set Status" variant="primary" menu-class="pl-0">
      <div v-if="loadingStatus" class="d-flex justify-content-center mb-3">
        <b-spinner label="Loading..." small></b-spinner>
      </div>
      <div v-if="!loadingStatus && !availableStatus.length && currentStatus">
        <p style="color: grey" class="mb-0">Empty</p>
      </div>
      <b-dropdown-item
        v-if="!loadingStatus"
        v-for="status in availableStatus"
        :key="status.label"
        @click="
          {
            selectedStatus = status;
            setStatusConfirmation(status.label);
          }
        "
        >{{ status.label }}</b-dropdown-item
      >
    </b-dropdown>
    <BarrelClearedModal
      v-if="showBarrelClearedModal"
      @closeModal="closeBarrelClearedModal"
      @customsDuty="acceptBarrelClearedModalDuty"
    />
    <ConfirmationModal
      :isLoadingConfirmation="loadingConfirmation"
      :isShowDialog="isShowConfirmationModal"
      :btnConfig="buttonConfiguration"
      :inputInfo="confirmationModalInfo"
      :inputColor="confirmationModalInfoColor"
      @eConfirm="setStatus"
      @eCancel="defaultCancelOperation"
    />
    <SnackbarNotifier
      :isShow="isShowSnackBar"
      :message="responseMessage"
      :snackType="snackbarType"
      :colour="snackBarColour"
      @eReset="resetSnackBar"
    />
  </div>
</template>

<script>
import { ApiService } from "@/services/ApiService";
import { error } from "@/services/LoggerService";
import BarrelClearedModal from "@/components/BarrelClearedModal.vue";
import ConfirmationModal from "@/components/ConfirmationModal.vue";
import ConfirmationModalMixin from "@/mixins/ConfirmationModalMixins";
import SnackbarNotifier from "@/components/SnackbarNotifier.vue";
import SnackbarNotifierMixin from "@/mixins/SnackbarNotifierMixins";
import mixins from "vue-typed-mixins";
import BarrelStatusMixin, { Status } from "@/mixins/BarrelStatusMixin";
export default mixins(
  SnackbarNotifierMixin,
  ConfirmationModalMixin,
  BarrelStatusMixin
).extend({
  name: "StatusDropdown",
  props: {
    areAllItemsPacked: {
      type: Boolean,
      required: true,
    },
    deliveryMethod: {
      type: String,
      required: true,
    },
    barrelId: {
      type: String,
      required: true,
    },
    currentStatus: {
      type: String,
    },
  },
  components: {
    ConfirmationModal,
    SnackbarNotifier,
    BarrelClearedModal,
  },
  data() {
    return {
      loadingStatus: false,
      allStatus: [],
      setting: false,
      selectedStatus: null,
      showBarrelClearedModal: false,
      barrelClearedPromise: null,
      setStatusMessageMap: {
        [Status.BarrelSealed]: this.barrelSealedStatusMessage,
        default: this.defaultSetStatusMessage
      }
    };
  },
  computed: {
    availableStatus() {
      const current = this.allStatus.find((x) => x.label == this.currentStatus);

      const noAvailableStatuses = ["Customs Charge"];

      let stat = [];
      if (noAvailableStatuses.includes(current?.label)) {
        return stat;
      }

      const hideNextStatus = ["Shipping Charge Paid"];

      current?.next.forEach((status) => {
        if(hideNextStatus.includes(status)){
          return;
        }
        let temp = this.allStatus.find((x) => (x.label == status));
        stat.push({ label: temp.label, path: temp.path });
      });

      if (
        stat.findIndex(
          (status) =>
            status.label === "Ready For Delivery" ||
            status.label === "Ready For Pickup"
        ) >= 0
      ) {
        return this.deliveryMethod === "pickup"
          ? [{ label: "Ready For Pickup", path: "readyForCustomer" }]
          : [{ label: "Ready For Delivery", path: "readyForCustomer" }];
      }
      return stat;
    },
  },
  methods: {
    setStatusConfirmation(label) {
      const title = "Set Status";

      let getMessage = this.setStatusMessageMap[label];

      if(!getMessage){
        getMessage = this.setStatusMessageMap["default"];
      }

      const message = getMessage(label);

      const confirmationInput = {
        title,
        message
      };
      this.showConfirmationModal(confirmationInput);
    },
    barrelSealedStatusMessage(label){
      if(this.areAllItemsPacked){
        return `<p>${this.defaultSetStatusMessage(label)}</p><p><strong style="color:red">Once Sealed, the barrel fullness will not be able to be updated.</strong></p>`;
      }

        return `<p>${this.defaultSetStatusMessage(label)}</p><p><strong style="color:red">Once Sealed, the barrel fullness will not be able to be updated.</strong></p><p><strong style="color:red">There are unpacked items. Any unpacked items will be moved to the customer items section.</strong></p>`;
    },
    defaultSetStatusMessage(label){
      return `Are you sure you want to set the barrel status to <b><em>${label}</em></b>?`
    },
    async updateBarrelStatusInput(barrelStatusInput) {
      const { status } = barrelStatusInput;

      if (status !== "clearance") {
        return barrelStatusInput;
      }

      if (this.selectedStatus.label === "Barrel Held") {
        barrelStatusInput.body = { clearanceStatus: "Held" };
        return barrelStatusInput;
      }

      barrelStatusInput.body = { clearanceStatus: "Cleared" };

      const duty = await this.getCustomsDutyInput();

      if (!duty && duty !== 0) {
        return null;
      }

      if (duty > 0) {
        barrelStatusInput.body.customsChargedCost = duty;
      }

      return barrelStatusInput;
    },
    getCustomsDutyInput() {
      this.showBarrelClearedModal = true;
      this.isShowConfirmationModal = false;

      return new Promise((resolve, reject) => {
        this.barrelClearedPromise = {
          resolve,
          reject,
        };
      });
    },
    acceptBarrelClearedModalDuty(event) {
      try {
        this.showBarrelClearedModal = false;
        if (!this.barrelClearedPromise) {
          return;
        }

        if (!event && event !== 0) {
          this.barrelClearedPromise.reject();
        }

        this.barrelClearedPromise.resolve(event);
      } catch (e) {
        error(e);
      }
    },
    closeBarrelClearedModal() {
      try {
        this.showBarrelClearedModal = false;
        if (!this.barrelClearedPromise?.resolve) {
          return;
        }

        this.barrelClearedPromise.resolve();
      } catch (e) {
        error(e);
      }
    },
    async setStatus() {
      try {
        this.setting = true;
        let barrelStatusInput = await this.updateBarrelStatusInput({
          status: this.selectedStatus.path,
          barrelId: this.barrelId,
        });

        if (!barrelStatusInput) {
          this.setting = false;
          return;
        }

        this.isShowConfirmationModal = true;
        this.startConfirmationLoading();


        //the readyForCustomer endpoint has a specific output, for the rest of the status endpoints an exception will be thrown if it fails
        const selectedStatusReadForCustomer = barrelStatusInput.status === "readyForCustomer";

        const result = await ApiService.Instance.setBarrelStatus(
          barrelStatusInput,
          selectedStatusReadForCustomer
        );

        if (!selectedStatusReadForCustomer || result.success) {
          this.stopConfirmationLoading();
          let updateDelay = 1000;
          const longUpdateStatus = "clearance";
          if (barrelStatusInput.status === longUpdateStatus) {
            updateDelay = 3000;
          }
          this.$emit("updated", updateDelay);
          return;
        }

        if (result.errorMessage) {
          this.stopConfirmationLoading();
          this.$emit("setStatusFailed", result.errorMessage);
          return;
        }

        throw new Error("Failed to set status");
      } catch (e) {
        this.stopConfirmationLoading();
        error(e);
        this.setting = false;
        this.showNotifier({
          message: `Failed To Set Status`,
          snackbarType: "error",
        });
      }
    },
    async getStatusList() {
      try {
        this.loadingStatus = true;
        var result = await ApiService.Instance.getAllBarrelStatus();
        if (result) this.allStatus = result;

        this.loadingStatus = false;
      } catch (e) {
        this.loadingStatus = false;
        error(e);
      }
    },
  },
  async beforeMount() {
    try {
      await this.getStatusList();
    } catch (e) {
      error(e);
    }
  },
});
</script>

<style></style>
